import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../hse/HsePageChrome";
import { HsePageBody } from "../hse/HsePageBody";
import { HseTabBar } from "../hse/HseTabBar";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { PMDialog } from "../../utils/PMDialog";
import { trainingApi } from "../../../api/training";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import TrainingGuideEditor from "./TrainingGuideEditor";
import TrainingAdminReports from "./TrainingAdminReports";
import TrainingAdminDeliveries from "./trainingAdminDeliveries";
import TrainingPlayer from "./TrainingPlayer";
import TrainingDependencyIssues from "./TrainingDependencyIssues";
import {
  Badge,
  buttonClass,
  primaryClass,
  Feedback,
  RemoteOptions,
  newKey,
  requirementKeys,
  statusKeys,
  useAlive,
} from "./trainingAdminShared";

const actionLabels = {
  revise: "trainingAdmin.revise",
  copy: "trainingAdmin.copy",
  deactivate: "trainingAdmin.deactivate",
  reactivate: "trainingAdmin.reactivate",
  archive: "trainingAdmin.archive",
};
const stateValues = ["draft", "published", "deactivated", "archived"];
const confirmMessages = {
  copy: "trainingAdmin.copyInfo",
  deactivate: "trainingAdmin.deactivateInfo",
  reactivate: "trainingAdmin.reactivateInfo",
  archive: "trainingAdmin.archiveInfo",
};

export default function TrainingAdminWorkspace({ enabled = true, contextKey, navigation }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("view");
  const tab =
    !enabled && !["audit", "deliveries"].includes(requestedTab)
      ? "reports"
      : ["guides", "reports", "audit", "deliveries"].includes(requestedTab)
        ? requestedTab
        : "guides";
  const query = searchParams.get("q") || "";
  const states = searchParams.get("state") || "";
  const requirement = searchParams.get("requirement") || "";
  const positionId = searchParams.get("position") || "";
  const ownerId = searchParams.get("owner") || "";
  const health = searchParams.get("health") || "";
  const offset = Math.max(0, Number(searchParams.get("offset")) || 0);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [blockedGuideCount, setBlockedGuideCount] = useState(0);
  const [issueGuide, setIssueGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [editor, setEditor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [action, setAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [actionImpact, setActionImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);
  const busyRef = useRef(false);
  const pending = useRef(null);
  const fetchSequence = useRef(0);
  const alive = useAlive();
  const changeParams = useCallback(
    (patch) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(patch).forEach(([key, value]) =>
            value ? next.set(key, value) : next.delete(key),
          );
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const load = useCallback(async () => {
    const sequence = ++fetchSequence.current;
    setLoading(true);
    try {
      const result = await trainingApi.guides({
        query,
        state: states,
        requirement,
        position_id: positionId,
        responsible_user_id: ownerId,
        health,
        offset,
        limit: 30,
      });
      if (alive.current && sequence === fetchSequence.current) {
        setRows(result.data || []);
        setCount(result.count || 0);
        setBlockedGuideCount(result.blocked_guide_count || 0);
        setError(null);
      }
    } catch (e) {
      if (alive.current && sequence === fetchSequence.current) {
        setError(normalizeError(e));
        setRows([]);
      }
      throw e;
    } finally {
      if (alive.current && sequence === fetchSequence.current)
        setLoading(false);
    }
  }, [query, states, requirement, positionId, ownerId, health, offset, alive]);
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => {
      load().catch(() => {});
    }, 250);
    return () => {
      clearTimeout(timer);
      fetchSequence.current += 1;
    };
  }, [load, enabled]);
  const filterChanged = useCallback(
    (filters) =>
      changeParams({
        q: filters.query,
        state: filters.statuses?.join(","),
        requirement: filters.requirement?.join(","),
        health: filters.health?.[0],
        offset: null,
      }),
    [changeParams],
  );
  const viewGuide = async (guide, mode) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await (mode === "preview"
        ? trainingApi.preview(guide.id)
        : trainingApi.guide(guide.id));
      if (!alive.current) return;
      if (mode === "preview") setPreview(result.data);
      else setEditor({ guide: result.data, key: newKey() });
    } catch (e) {
      if (alive.current) setError(normalizeError(e));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const mutate = async (request) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setActionError(null);
    setActionMessage(null);
    pending.current = request;
    try {
      const result = await request.run();
      if (!alive.current) return;
      pending.current = null;
      setActionMessage(t("trainingAdmin.updated"));
      try {
        await load();
      } catch {
        if (alive.current)
          setActionMessage(
            `${t("trainingAdmin.updated")}. ${t("trainingAdmin.refreshWarning")}`,
          );
      }
      if (alive.current && ["copy", "revise"].includes(request.kind)) {
        setAction(null);
        setEditor({ guide: result.data, key: newKey() });
      }
    } catch (e) {
      if (alive.current) setActionError(normalizeError(e));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const perform = () => {
    const { kind, guide } = action;
    const payload = { request_key: newKey() };
    const methods = {
      revise: "newRevision",
      copy: "copyGuide",
      deactivate: "deactivate",
      reactivate: "reactivate",
      archive: "archive",
    };
    mutate({ kind, run: () => trainingApi[methods[kind]](guide.id, payload) });
  };
  const chooseAction = async (kind, guide) => {
    setAction({ kind, guide });
    setActionError(null);
    setActionMessage(null);
    setActionImpact(null);
    pending.current = null;
    if (kind !== "reactivate") return;
    busyRef.current = true;
    setBusy(true);
    try {
      const [diff, users] = await Promise.all([
        trainingApi.revisionDiff(guide.id, { revision: "current" }),
        trainingApi.audiencePreview(guide.id, {
          revision: "current",
          limit: 1,
        }),
      ]);
      if (alive.current)
        setActionImpact({ ...diff.data, affected_users: users.count });
    } catch (e) {
      if (alive.current) setActionError(normalizeError(e));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const closeAction = () => {
    if (!busy && !pending.current) setAction(null);
    else if (
      !busy &&
      actionError?.httpStatus >= 400 &&
      actionError.httpStatus < 500
    ) {
      pending.current = null;
      setAction(null);
    }
  };
  return (
      <HsePageChrome
        title={t("trainingAdmin.title")}
        subtitle={t("trainingAdmin.intro")}
        listCount={tab === "guides" ? count : undefined}
        mainButton={
          enabled
            ? {
                label: t("trainingAdmin.newGuide"),
                icon: PlusIcon,
                action: () => {
                  setEditor({ guide: null, key: newKey() });
                  setMessage(null);
                },
              }
            : undefined
        }
      >
      <HsePageBody className="min-w-0 space-y-5 pb-8">
      {navigation}
      <HseTabBar
        wrap
        ariaLabel={t("trainingAdmin.title")}
        activeId={tab}
        onSelect={({ id }) => changeParams({ view: id })}
        tabs={[
          ["guides", "trainingAdmin.guides"],
          ["reports", "trainingAdmin.reports"],
          ["audit", "trainingAdmin.audit"],
          ["deliveries", "trainingAdmin.deliveries"],
        ]
          .filter(([value]) => enabled || value !== "guides")
          .map(([id, key]) => ({ id, label: t(key) }))}
      />
      {tab === "guides" ? (
        <>
          {!error && blockedGuideCount > 0 && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
            <div><p className="font-bold">{t('trainingAdmin.healthSummary', { count: blockedGuideCount })}</p><p className="mt-1">{t('trainingAdmin.healthSummaryHint')}</p></div>
            <button className={buttonClass} onClick={() => changeParams({ health: 'blocked', q: null, state: null, requirement: null, position: null, owner: null, offset: null })}>{t('trainingAdmin.healthShow')}</button>
          </div>}
          {!enabled && (
            <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
              {t("trainingAdmin.readOnly")}
            </p>
          )}
          <ModuleFilter
            filterCard
            initialFilters={{
              query,
              statuses: states ? states.split(",") : [],
              requirement: requirement ? requirement.split(",") : [],
              health: health ? [health] : [],
            }}
            onFiltersChange={filterChanged}
            statusOptions={stateValues.map((value) => ({
              value,
              label: t(statusKeys[value]),
            }))}
            customDropdownFilters={[
              { key: 'health', label: 'trainingAdmin.healthTitle', options: [{ value: 'blocked', label: t('trainingAdmin.healthNeedsFix') }] },
              {
                key: "requirement",
                label: "trainingAdmin.requirement",
                options: Object.entries(requirementKeys).map(
                  ([value, label]) => ({ value, label: t(label) }),
                ),
              },
            ]}
          />
          <div className="flex flex-wrap gap-2">
            <button className={buttonClass} disabled={loading} onClick={() => load().catch(() => {})}>{t('trainingAdmin.healthRecheck')}</button>
            <button className={buttonClass} onClick={() => setPicker("users")}>
              {t("trainingAdmin.owner")}
              {ownerId ? ` #${ownerId}` : ""}
            </button>
            <button
              className={buttonClass}
              onClick={() => setPicker("positions")}
            >
              {t("trainingAdmin.positions")}
              {positionId ? ` #${positionId}` : ""}
            </button>
            {(ownerId || positionId) && (
              <button
                className={buttonClass}
                onClick={() =>
                  changeParams({ owner: null, position: null, offset: null })
                }
              >
                {t("trainingAdmin.remove")}
              </button>
            )}
          </div>
          <Feedback error={error} message={message}>
            {error && (
              <button
                className={`${buttonClass} mt-3`}
                onClick={() => load().catch(() => {})}
              >
                {t("trainingAdmin.reload")}
              </button>
            )}
          </Feedback>
          <div
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)] md:rounded-[18px]"
            aria-busy={loading}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[850px] text-left text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    {[
                      "trainingAdmin.guide",
                      "trainingAdmin.status",
                      "trainingAdmin.revision",
                      "trainingAdmin.requirement",
                      "trainingAdmin.actions",
                    ].map((key) => (
                      <th className="px-5 py-4" key={key}>
                        {t(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((guide) => (
                    <tr
                      key={guide.id}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="max-w-sm px-5 py-4">
                        <button
                          className="text-left font-semibold text-blue-700 hover:underline"
                          disabled={busy}
                          onClick={() =>
                            viewGuide(
                              guide,
                              enabled && guide.draft_revision
                                ? "edit"
                                : "preview",
                            )
                          }
                        >
                          {guide.title}
                        </button>
                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
                          {guide.description}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <Badge status={guide.state} />
                        {guide.blocked_user_count > 0 && <button className="mt-2 block rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-left text-xs font-bold text-rose-800 hover:bg-rose-100" onClick={() => setIssueGuide(guide)}>{t('trainingAdmin.healthBlocked', { count: guide.blocked_user_count })}<span className="mt-1 block underline">{t('trainingAdmin.healthDetails')}</span></button>}
                        {guide.draft_revision && guide.current_revision_id && (
                          <div className="mt-1">
                            <Badge status="draft" />
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 tabular-nums">
                        {guide.current_revision?.number ||
                          guide.draft_revision?.number ||
                          "—"}
                      </td>
                      <td className="px-5 py-4">
                        {t(
                          requirementKeys[
                            (guide.current_revision || guide.draft_revision)
                              ?.policy?.requirement
                          ] || "trainingAdmin.optional",
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            className={buttonClass}
                            disabled={busy}
                            onClick={() => viewGuide(guide, "preview")}
                          >
                            {t("trainingAdmin.preview")}
                          </button>
                          {enabled && guide.state !== "archived" && (
                            <>
                              <button
                                className={buttonClass}
                                disabled={busy}
                                onClick={() =>
                                  guide.draft_revision
                                    ? viewGuide(guide, "edit")
                                    : chooseAction("revise", guide)
                                }
                              >
                                {t(
                                  guide.draft_revision
                                    ? "trainingAdmin.edit"
                                    : "trainingAdmin.revise",
                                )}
                              </button>
                              <button
                                className={buttonClass}
                                disabled={busy}
                                onClick={() => chooseAction("copy", guide)}
                              >
                                {t("trainingAdmin.copy")}
                              </button>
                              {guide.state === "published" && (
                                <button
                                  className={`${buttonClass} !text-rose-700`}
                                  disabled={busy}
                                  onClick={() =>
                                    chooseAction("deactivate", guide)
                                  }
                                >
                                  {t("trainingAdmin.deactivate")}
                                </button>
                              )}
                              {guide.state === "deactivated" && (
                                <button
                                  className={buttonClass}
                                  disabled={busy}
                                  onClick={() =>
                                    chooseAction("reactivate", guide)
                                  }
                                >
                                  {t("trainingAdmin.reactivate")}
                                </button>
                              )}
                              {["draft", "deactivated"].includes(
                                guide.state,
                              ) && (
                                <button
                                  className={`${buttonClass} !text-rose-700`}
                                  disabled={busy}
                                  onClick={() => chooseAction("archive", guide)}
                                >
                                  {t("trainingAdmin.archive")}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && !rows.length && (
              <div className="space-y-2 p-12 text-center">
                <h2 className="text-lg font-bold text-slate-800">
                  {t(
                    query || states
                      ? "trainingAdmin.noResults"
                      : "trainingAdmin.emptyGuides",
                  )}
                </h2>
                <p className="text-sm text-slate-500">
                  {t("trainingAdmin.emptyHint")}
                </p>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 p-3">
              <span className="text-xs tabular-nums text-slate-500">
                {count ? offset + 1 : 0}–{Math.min(offset + rows.length, count)}{" "}
                / {count}
              </span>
              <div className="flex gap-2">
                <button
                  className={buttonClass}
                  disabled={loading || offset === 0}
                  onClick={() =>
                    changeParams({ offset: Math.max(0, offset - 30) })
                  }
                >
                  {t("trainingAdmin.previous")}
                </button>
                <button
                  className={buttonClass}
                  disabled={loading || offset + 30 >= count}
                  onClick={() => changeParams({ offset: offset + 30 })}
                >
                  {t("trainingAdmin.next")}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : tab === "deliveries" ? (
        <TrainingAdminDeliveries />
      ) : (
        <TrainingAdminReports
          key={tab}
          enabled={enabled}
          auditOnly={tab === "audit"}
        />
      )}
      {editor && (
        <TrainingGuideEditor
          key={editor.key}
          guide={editor.guide}
          onClose={() => setEditor(null)}
          onSaved={load}
          contextKey={contextKey}
        />
      )}
      {issueGuide && <TrainingDependencyIssues key={`${contextKey}:${issueGuide.id}`} guide={issueGuide}
        onClose={() => { setIssueGuide(null); load().catch(() => {}); }}
        onChecked={(id, blockedCount) => setRows((previous) => previous.map((row) => row.id === id ? { ...row, blocked_user_count: blockedCount } : row))}
        onDeactivate={() => { setIssueGuide(null); chooseAction('deactivate', issueGuide); }}
        onEdit={() => { setIssueGuide(null); issueGuide.draft_revision ? viewGuide(issueGuide, 'edit') : chooseAction('revise', issueGuide); }} />}
      {preview && (
        <TrainingPlayer
          open
          preview
          previewData={preview}
          onClose={() => setPreview(null)}
          contextKey={contextKey}
        />
      )}
      {picker && (
        <PMDialog
          open
          title={t(
            picker === "users"
              ? "trainingAdmin.owner"
              : "trainingAdmin.positions",
          )}
          onClose={() => setPicker(null)}
          closeOnOutsideClick
        >
          <RemoteOptions
            kind={picker}
            label={t(
              picker === "users"
                ? "trainingAdmin.owner"
                : "trainingAdmin.positions",
            )}
            multiple={false}
            selected={
              Number(picker === "users" ? ownerId : positionId)
                ? [Number(picker === "users" ? ownerId : positionId)]
                : []
            }
            onChange={(ids) => {
              changeParams({
                [picker === "users" ? "owner" : "position"]: ids[0],
                offset: null,
              });
              setPicker(null);
            }}
          />
        </PMDialog>
      )}
      {action && (
        <PMDialog
          open
          title={t(actionLabels[action.kind])}
          onClose={closeAction}
          closeOnOutsideClick
        >
          <div className="space-y-5">
            <h2 className="text-lg font-bold text-slate-900">
              {action.guide.title}
            </h2>
            <p className="text-sm leading-6 text-slate-600">
              {t(confirmMessages[action.kind] || "trainingAdmin.publishInfo")}
            </p>
            <Feedback error={actionError} message={actionMessage} />
            {action.kind === "reactivate" && actionImpact && (
              <div className="rounded-xl bg-slate-50 p-4 text-sm">
                <p>
                  {t("trainingAdmin.affectedUsers")}:{" "}
                  {actionImpact.affected_users}
                </p>
                {actionImpact.access_issues?.length > 0 && (
                  <p role="alert" className="mt-2 text-rose-800">
                    {t("trainingAdmin.dependencyProblems")}
                  </p>
                )}
              </div>
            )}
            {action.kind === "reactivate" &&
              actionError &&
              !pending.current && (
                <button
                  className={buttonClass}
                  disabled={busy}
                  onClick={() => chooseAction(action.kind, action.guide)}
                >
                  {t("trainingAdmin.reload")}
                </button>
              )}
            {actionError &&
            (!actionError.httpStatus || actionError.httpStatus >= 500) &&
            pending.current ? (
              <button
                className={primaryClass}
                disabled={busy}
                onClick={() => mutate(pending.current)}
              >
                {t("trainingAdmin.checkStatus")}
              </button>
            ) : (
              <div className="flex justify-end gap-3">
                <button
                  className={buttonClass}
                  disabled={busy}
                  onClick={closeAction}
                >
                  {t(
                    actionMessage
                      ? "trainingAdmin.close"
                      : "trainingAdmin.cancel",
                  )}
                </button>
                {!actionMessage && (
                  <button
                    className={primaryClass}
                    disabled={
                      busy ||
                      (action.kind === "reactivate" &&
                        (!actionImpact ||
                          Boolean(actionImpact.access_issues?.length)))
                    }
                    onClick={perform}
                  >
                    {t(actionLabels[action.kind])}
                  </button>
                )}
              </div>
            )}
          </div>
        </PMDialog>
      )}
      </HsePageBody>
      </HsePageChrome>
  );
}
